import { keyboard } from '@toggle/helpers';
import { Entity, EntitySearchType } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInput } from '~/components/chat-input/ChatInput';
import { useRows } from '~/components/chat-input/useRows';
import { useTagsSearch } from '~/hooks/use-tags-search/useTagsSearch';

import * as S from './ChatWatchlistManagerWidget.styles';
import { ChatWatchlistItem } from './components/chat-watchlist-item/ChatWatchlistItem';

export interface ChatWatchlistManagerProps {
  isPending: boolean;
  onSelectItem: (item: Entity, isInWatchlist: boolean) => void;
  onChatSubmit: (value: string) => void;
  getIsInWatchlist: (item: Entity) => boolean;
}

const CHAT_MESSAGE_REGEX = /[\n\r,]/;

export const ChatWatchlistManager = ({
  isPending,
  onSelectItem,
  onChatSubmit,
  getIsInWatchlist,
}: ChatWatchlistManagerProps) => {
  const { t } = useTranslation(['widget', 'analyze', 'countries']);
  const [inputValue, setInputValue] = useState('');
  const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
  const { rows, updateRows } = useRows({ value: inputValue, maxRows: 20 });

  const isAssetNameMessage = (value: string) => !CHAT_MESSAGE_REGEX.test(value);

  const {
    tags: tagList,
    searchTags,
    clearTags,
    loading: tagsSearchPending,
  } = useTagsSearch({
    searchType: EntitySearchType.Entity,
    additionalConditionChecker: isAssetNameMessage,
  });

  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setInputValue(value);
    searchTags(value);
  };

  const onClear = () => {
    setInputValue('');
    setFocusedItemIndex(-1);
    clearTags();
  };

  const handleDropdownKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (items.length) {
      const maxIndex = items.length - 1;
      if (e.key === keyboard.up.key) {
        e.preventDefault();
        const index = focusedItemIndex === 0 ? maxIndex : focusedItemIndex - 1;
        setFocusedItemIndex(index);
      } else if (e.key === keyboard.down.key || e.key === keyboard.tab.key) {
        e.preventDefault();
        const index = focusedItemIndex === maxIndex ? 0 : focusedItemIndex + 1;
        setFocusedItemIndex(index);
      } else if (focusedItemIndex >= 0 && e.key === keyboard.enter.key) {
        e.preventDefault();
        onSelectItem(
          items[focusedItemIndex],
          getIsInWatchlist(items[focusedItemIndex])
        );
        setInputValue('');
        setFocusedItemIndex(-1);
      }
    } else if (inputValue) {
      if (e.key === keyboard.enter.key) {
        e.preventDefault();
        handleChatSubmit();
      }
      setFocusedItemIndex(-1);
    }
  };

  const handleChatSubmit = () => {
    if (isPending) {
      return;
    }
    if (inputValue) {
      const question = inputValue.trim();
      onChatSubmit(question);
      setInputValue('');
    }
  };

  const items = tagList?.slice(0, 5);
  // if there are no items either because inputValue has comma or line break OR search results are empty
  // --> display chat submit
  const showChatSubmit =
    !tagsSearchPending && !items.length && inputValue.length > 1;

  return (
    <S.StyledContainer>
      <ChatInput
        placeholder={t('widget:watchlistManager.chatPlaceholder')}
        value={inputValue}
        isMessagePending={isPending}
        rows={rows}
        onChange={onInputChange}
        updateRows={updateRows}
        displayReset={false}
        onKeyDown={handleDropdownKeyDown}
      >
        {!!items?.length && (
          <S.StyledIcon iconName="CloseCircle" size={16} onClick={onClear} />
        )}
      </ChatInput>
      {showChatSubmit ? (
        <S.SubmitButton
          variant="primary"
          iconName="ArrowDownLeft"
          size="large"
          iconPosition="right"
          label={t('widget:watchlistManager.submit')}
          data-testid="chat-watchlist-submit-btn"
          onClick={handleChatSubmit}
        />
      ) : (
        <S.AssetsList>
          {items.map((item, index) => (
            <ChatWatchlistItem
              key={item.id}
              isInWatchlist={getIsInWatchlist(item)}
              entity={item}
              onClick={onSelectItem}
              isFocused={focusedItemIndex === index}
            />
          ))}
        </S.AssetsList>
      )}
    </S.StyledContainer>
  );
};
